import { all, call, put, SagaReturnType, select, takeEvery } from 'redux-saga/effects'

import { actions, selectors } from '..'
import { getAuthCookie, setAuthCookie } from '../../helpers/CookieHelpers'
import ApiSagas from '../api/sagas'
import { services } from '../../graphql'
import { User } from '../../graphql/generated/graphql'

export default class AuthSagas {
  static *onInit() {
    const cookie = getAuthCookie()

    if (cookie) {
      yield put(actions.auth.setImpersonate(cookie?.impersonate ?? false))
      yield put(actions.auth.setToken(cookie?.token))
    } else {
      yield put(actions.auth.setImpersonate(false))
      yield put(actions.auth.setToken(''))
      yield put(actions.declaration.reset())
    }

    const isConnected: SagaReturnType<typeof selectors.auth.isConnected> = yield select(
      selectors.auth.isConnected
    )

    if (isConnected) {
      yield call(AuthSagas.getUserFromToken)
    }
  }

  static *getUserFromToken() {
    yield put(actions.auth.setIsLoading(true))
    const rs = yield* ApiSagas.call(services.user.queries.me)

    if (!rs?.errors) {
      yield put(actions.auth.setUser(rs?.data))
    } else {
      yield put(actions.auth.resetAuth())
    }

    yield put(actions.auth.setIsLoading(false))
  }

  static *onLoginRequest({ payload }: ReturnType<typeof actions.auth.loginRequest>) {
    const rs = yield* ApiSagas.call(services.auth.mutations.login, {
      email: payload.email,
      password: payload.password,
    })

    if (rs.errors) {
      yield put(actions.auth.loginError(rs.errors))
      return
    }

    if (rs.data) {
      yield all([
        put(actions.auth.socialLoginError(null)),
        put(actions.auth.loginError(null)),
        put(actions.auth.loginSuccess()),
      ])
    }
  }

  static *onSocialLoginRequest({ payload }: ReturnType<typeof actions.auth.socialLoginRequest>) {
    const rs = yield* ApiSagas.call(services.auth.mutations.socialLogin, payload)

    if (rs.errors) {
      yield put(actions.auth.socialLoginError(rs.errors))
      yield put(actions.auth.loginError(rs.errors))
      return
    }

    if (rs.data) {
      const user = rs.data as User
      const token = user?.token ?? ''
      setAuthCookie({ token, impersonate: false })
      yield all([
        put(actions.auth.socialLoginError(null)),
        put(actions.auth.loginError(null)),
        put(actions.auth.setToken(token)),
        put(actions.auth.setUser(user)),
        put(actions.auth.loginSuccess()),
        put(actions.auth.socialLoginSuccess(user)),
      ])
    }
  }

  static *onForgotRequest({ payload }: ReturnType<typeof actions.auth.forgotRequest>) {
    const rs = yield* ApiSagas.call(services.auth.mutations.forgotPassword, {
      email: payload.email,
    })

    if (rs.errors) {
      yield put(actions.auth.forgotError(rs.errors))
      return
    }

    if (rs.data) {
      yield put(actions.auth.forgotSuccess())
    }
  }

  static *onVerifyTwoFactorRequest({
    payload,
  }: ReturnType<typeof actions.auth.verifyTwoFactorRequest>) {
    const rs = yield* ApiSagas.call(services.auth.mutations.verifyTwoFactor, {
      email: payload.email,
      code: payload.code,
    })

    if (rs.errors) {
      yield put(actions.auth.verifyTwoFactorError(rs.errors))
      return
    }

    if (rs.data) {
      const user = rs.data as User
      const token = user?.token ?? ''
      setAuthCookie({ token, impersonate: false })
      yield all([
        put(actions.auth.socialLoginError(null)),
        put(actions.auth.loginError(null)),
        put(actions.auth.setToken(token)),
        put(actions.auth.setUser(user)),
        put(actions.auth.loginSuccess()),
        put(actions.auth.verifyTwoFactorSuccess()),
      ])
    }
  }

  static *onResetRequest({ payload }: ReturnType<typeof actions.auth.resetRequest>) {
    const rs = yield* ApiSagas.call(services.auth.mutations.resetPassword, {
      token: payload.token,
      email: payload.email,
      password: payload.password,
    })

    if (rs.errors) {
      yield put(actions.auth.resetError(rs.errors))
      return
    }

    if (rs.data) {
      const user = rs.data as User
      const token = user?.token ?? ''

      yield all([
        put(actions.auth.resetSuccess()),
        put(actions.auth.setToken(token)),
        put(actions.auth.setUser(user)),
      ])
    }
  }

  static *onLogoutRequest() {
    setAuthCookie({ token: '' })
    yield put(actions.auth.resetAuth())
  }

  static *onResetAuth() {
    setAuthCookie({ token: '' })
    yield put(actions.declaration.reset())
  }

  static *listeners() {
    yield all([
      takeEvery(actions.auth.loginRequest, this.onLoginRequest),
      takeEvery(actions.auth.socialLoginRequest, this.onSocialLoginRequest),
      takeEvery(actions.auth.forgotRequest, this.onForgotRequest),
      takeEvery(actions.auth.resetRequest, this.onResetRequest),
      takeEvery(actions.auth.logout, this.onLogoutRequest),
      takeEvery(actions.auth.loadUser, this.getUserFromToken),
      takeEvery(actions.auth.resetAuth, this.onResetAuth),
      takeEvery(actions.auth.verifyTwoFactorRequest, this.onVerifyTwoFactorRequest),
    ])
  }
}
